import { AsyncPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  ViewContainerRef,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { SafeResourceUrl } from '@angular/platform-browser';

import { I18nService } from '@core/i18n-legacy';
import type { DialogComponentI, IconName } from '@core/user-interface-legacy';
import {
  buildDialogConfig,
  DialogBodyComponent,
  DialogCloseComponent,
  DialogRef,
  DialogWidth,
  HeadingComponent,
} from '@core/user-interface-legacy';
import { ToObservablePipe } from '@core/utils-legacy';

import type { WalkthroughComponentProperties } from '../../types';
import { WalkthroughDialogFooterComponent } from './walkthrough-dialog-footer/walkthrough-dialog-footer.component';

interface WalkthroughDialogData {
  ctaText: string;
  ctaHandler: () => void;
  ofText: string;
  heading: string;
  description: string;
  imageUrl: string;
  dialogHelpArticle: string;
  learnMoreText: string;
  displayCtaOnly: boolean;
  showCloseButton: boolean;
  showSkipButton: boolean;
}

@Component({
  selector: 'rt-walkthrough-dialog',
  imports: [
    DialogBodyComponent,
    DialogCloseComponent,
    WalkthroughDialogFooterComponent,
    HeadingComponent,
    AsyncPipe,
    ToObservablePipe,
  ],
  templateUrl: './walkthrough-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './walkthrough-dialog.component.less',
})
export class WalkthroughDialogComponent
  implements WalkthroughComponentProperties, DialogComponentI<WalkthroughDialogData, void>
{
  private readonly i18n = inject(I18nService);
  private readonly viewRef = inject(ViewContainerRef);
  private readonly dialogRef = inject(DialogRef, { optional: true });

  public readonly currentStep = input<number>(undefined!);
  public readonly stepsTotal = input<number>(undefined!);
  public readonly ctaIcon = input<IconName>();
  public readonly closeText = input<string>(this.i18n.translate('app.walkthrough.dialog.back'));
  public readonly closeIcon = input<IconName | undefined>('chevron-left');
  public readonly showStepCounter = input<boolean>(true);
  public readonly skipText = input<string>(this.i18n.translate('app.common.skip'));

  @Input() public videoUrl?: SafeResourceUrl = undefined;
  @Input() public imageUrl?: string = undefined;

  @Input() public heading: string;
  @Input() public description: string;

  @Input() public ctaText: string;
  @Input() public ctaHandler?: () => void;
  @Input() public ofText: string = this.i18n.translate('app.common.of');

  @Input() public displayCtaOnly: boolean = false;
  @Input() public showCloseButton: boolean = false;
  @Input() public showSkipButton: boolean = false;

  @Input() public dialogHelpArticle: string;
  @Input() public learnMoreText: string;

  public cancel = new EventEmitter<void>();
  public finish = new EventEmitter<void>();
  public next = new EventEmitter<void>();

  public dialogConfig = buildDialogConfig({
    width: DialogWidth.Medium,
    viewContainerRef: this.viewRef,
  });

  public setDialogData(data: WalkthroughDialogData): void {
    this.ctaText = data.ctaText;
    this.ofText = data.ofText;
    this.heading = data.heading;
    this.description = data.description;
    this.imageUrl = data.imageUrl;
    this.displayCtaOnly = data.displayCtaOnly;
    this.showCloseButton = data.showCloseButton;
    this.showSkipButton = data.showSkipButton;
    this.dialogHelpArticle = data.dialogHelpArticle;
    this.learnMoreText = data.learnMoreText;
    this.ctaHandler = data.ctaHandler;
  }

  public close() {
    this.cancel.emit();
  }

  public handleCtaClicked(): void {
    if (!!this.ctaHandler) {
      this.ctaHandler();
    }
    this.next.emit();
  }
}
